import { useEffect, useState } from 'react';
import { Users, MousePointer, TrendingUp, Globe, Send, MessageCircle, ChevronLeft, ChevronRight, CalendarIcon } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { getStatsOverview, getTrafficStats, getManagerStats, getVisitorsStats } from '@/lib/api';
import { ru } from 'date-fns/locale';
import { format, subDays } from 'date-fns';

export default function AdminDashboard() {
  const [overview, setOverview] = useState(null);
  const [traffic, setTraffic] = useState([]);
  const [managers, setManagers] = useState([]);
  const [visitors, setVisitors] = useState([]);
  const [visitorCount, setVisitorCount] = useState(0);
  const [visitorPage, setVisitorPage] = useState(1);
  const [visitorPageCount, setVisitorPageCount] = useState(1);
  const [busy, setBusy] = useState(true);
  
  // Date filter state
  const [dateFilter, setDateFilter] = useState('today'); // 'all', 'today', 'yesterday', 'custom'
  const [dateRange, setDateRange] = useState({ from: null, to: null });
  const [calendarOpen, setCalendarOpen] = useState(false);

  // Get date strings for API calls
  const currentDateWindow = () => {
    const today = new Date();
    const yesterday = subDays(today, 1);
    
    switch (dateFilter) {
      case 'today':
        return { startDate: format(today, 'yyyy-MM-dd'), endDate: format(today, 'yyyy-MM-dd') };
      case 'yesterday':
        return { startDate: format(yesterday, 'yyyy-MM-dd'), endDate: format(yesterday, 'yyyy-MM-dd') };
      case 'custom':
        if (dateRange.from) {
          return {
            startDate: format(dateRange.from, 'yyyy-MM-dd'),
            endDate: dateRange.to ? format(dateRange.to, 'yyyy-MM-dd') : format(dateRange.from, 'yyyy-MM-dd')
          };
        }
        return { startDate: null, endDate: null };
      default:
        return { startDate: null, endDate: null };
    }
  };

  // Get display label for date filter
  const currentDateLabel = () => {
    switch (dateFilter) {
      case 'today':
        return 'Сегодня';
      case 'yesterday':
        return 'Вчера';
      case 'custom':
        if (dateRange.from) {
          if (dateRange.to && dateRange.from !== dateRange.to) {
            return `${format(dateRange.from, 'dd.MM')} - ${format(dateRange.to, 'dd.MM')}`;
          }
          return format(dateRange.from, 'dd.MM.yyyy');
        }
        return 'Выбрать даты';
      default:
        return 'Все время';
    }
  };

  useEffect(() => {
    fetchStats();
  }, [dateFilter, dateRange]);

  useEffect(() => {
    fetchVisitors();
  }, [visitorPage, dateFilter, dateRange]);

  const fetchStats = async () => {
    try {
      setBusy(true);
      const { startDate, endDate } = currentDateWindow();
      
      const [overviewRes, trafficRes, managersRes] = await Promise.all([
        getStatsOverview(startDate, endDate),
        getTrafficStats(7),
        getManagerStats(startDate, endDate)
      ]);

      setOverview(overviewRes.data);
      setTraffic(trafficRes.data);
      setManagers(managersRes.data);
    } catch (err) {
      console.error('Failed to load stats:', err);
    } finally {
      setBusy(false);
    }
  };

  const fetchVisitors = async () => {
    try {
      const { startDate, endDate } = currentDateWindow();
      const res = await getVisitorsStats(visitorPage, 20, startDate, endDate);
      setVisitors(res.data.visitors);
      setVisitorCount(res.data.total);
      setVisitorPageCount(res.data.pages);
    } catch (err) {
      console.error('Failed to load visitors:', err);
    }
  };

  const applyDatePreset = (filter) => {
    setDateFilter(filter);
    setVisitorPage(1);
    if (filter !== 'custom') {
      setDateRange({ from: null, to: null });
    }
  };

  const applyDateRange = (range) => {
    setDateRange(range || { from: null, to: null });
    if (range?.from) {
      setDateFilter('custom');
      setVisitorPage(1);
    }
  };

  const toShortDateTime = (timestamp) => {
    if (!timestamp) return '-';
    const date = new Date(timestamp);
    return date.toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (busy) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Дашборд</h1>
          <p className="text-slate-500">Статистика и аналитика</p>
        </div>
        
        {/* Date Filter */}
        <div className="flex flex-wrap items-center gap-2" data-testid="date-filter">
          <Button
            variant={dateFilter === 'all' ? 'default' : 'outline'}
            size="sm"
            onClick={() => applyDatePreset('all')}
            data-testid="filter-all"
          >
            Все время
          </Button>
          <Button
            variant={dateFilter === 'today' ? 'default' : 'outline'}
            size="sm"
            onClick={() => applyDatePreset('today')}
            data-testid="filter-today"
          >
            Сегодня
          </Button>
          <Button
            variant={dateFilter === 'yesterday' ? 'default' : 'outline'}
            size="sm"
            onClick={() => applyDatePreset('yesterday')}
            data-testid="filter-yesterday"
          >
            Вчера
          </Button>
          
          <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
            <PopoverTrigger asChild>
              <Button
                variant={dateFilter === 'custom' ? 'default' : 'outline'}
                size="sm"
                className="min-w-[140px]"
                data-testid="filter-custom"
              >
                <CalendarIcon className="w-4 h-4 mr-2" />
                {dateFilter === 'custom' && dateRange.from ? currentDateLabel() : 'Произвольно'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end">
              <Calendar
                mode="range"
                selected={dateRange}
                onSelect={(range) => {
                  applyDateRange(range);
                  if (range?.to) setCalendarOpen(false);
                }}
                locale={ru}
                disabled={(date) => date > new Date()}
                numberOfMonths={1}
              />
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Визиты</p>
                <p className="text-2xl font-bold">{overview?.total_visits || 0}</p>
                <p className="text-xs text-slate-400">
                  {dateFilter === 'all' ? `+${overview?.today_visits || 0} сегодня` : currentDateLabel()}
                </p>
              </div>
              <Users className="w-8 h-8 text-slate-300" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Клики</p>
                <p className="text-2xl font-bold">{overview?.total_clicks || 0}</p>
                <p className="text-xs text-slate-400">
                  {dateFilter === 'all' ? `+${overview?.today_clicks || 0} сегодня` : currentDateLabel()}
                </p>
              </div>
              <MousePointer className="w-8 h-8 text-sky-300" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Конверсия</p>
                <p className="text-2xl font-bold">{overview?.conversion_rate || 0}%</p>
                <p className="text-xs text-slate-400">клики/визиты</p>
              </div>
              <TrendingUp className="w-8 h-8 text-green-300" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Уникальных</p>
                <p className="text-2xl font-bold">{visitorCount}</p>
                <p className="text-xs text-slate-400">
                  {dateFilter === 'all' ? 'посетителей' : currentDateLabel()}
                </p>
              </div>
              <Globe className="w-8 h-8 text-amber-300" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Managers Stats */}
      <Card>
        <CardHeader>
          <CardTitle>Клики по менеджерам</CardTitle>
        </CardHeader>
        <CardContent>
          {managers.length === 0 ? (
            <p className="text-slate-500 text-center py-4">Нет данных</p>
          ) : (
            <div className="space-y-3">
              {managers.map((m, i) => (
                <div key={m.manager_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div className="font-medium text-slate-900">{m.manager_name}</div>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-1 text-sm">
                      <Send className="w-4 h-4 text-sky-500" />
                      <span className="text-slate-600">{m.telegram}</span>
                    </div>
                    <div className="flex items-center gap-1 text-sm">
                      <MessageCircle className="w-4 h-4 text-green-500" />
                      <span className="text-slate-600">{m.whatsapp}</span>
                    </div>
                    <div className="text-sm font-semibold text-slate-900 min-w-[60px] text-right">
                      Всего: {m.total}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Visitors List */}
      <Card>
        <CardHeader>
          <CardTitle>Посетители (уникальные)</CardTitle>
        </CardHeader>
        <CardContent>
          {visitors.length === 0 ? (
            <p className="text-slate-500 text-center py-4">Нет данных</p>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 px-2 font-medium text-slate-500">Дата</th>
                      <th className="text-left py-2 px-2 font-medium text-slate-500">Страна</th>
                      <th className="text-left py-2 px-2 font-medium text-slate-500">IP</th>
                      <th className="text-center py-2 px-2 font-medium text-slate-500" title="Всего заходов с этого IP">Заходов</th>
                      <th className="text-left py-2 px-2 font-medium text-slate-500">Устройство</th>
                      <th className="text-center py-2 px-2 font-medium text-slate-500">
                        <Send className="w-4 h-4 inline text-sky-500" />
                      </th>
                      <th className="text-center py-2 px-2 font-medium text-slate-500">
                        <MessageCircle className="w-4 h-4 inline text-green-500" />
                      </th>
                      <th className="text-left py-2 px-2 font-medium text-slate-500">Менеджер</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visitors.map((v, i) => (
                      <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-2 px-2 text-slate-600">{toShortDateTime(v.last_visit)}</td>
                        <td className="py-2 px-2">
                          <span className="text-slate-900">{v.country}</span>
                          {v.city && <span className="text-slate-400 text-xs ml-1">{v.city}</span>}
                        </td>
                        <td className="py-2 px-2 text-slate-500 font-mono text-xs">{v.ip || '-'}</td>
                        <td className="py-2 px-2 text-center">
                          {v.ip_visit_count > 1 ? (
                            <span className="inline-flex items-center justify-center min-w-[1.5rem] px-2 h-6 bg-indigo-100 text-indigo-700 rounded-full text-xs font-medium">
                              {v.ip_visit_count}
                            </span>
                          ) : (
                            <span className="text-slate-400 text-xs">{v.ip_visit_count || 1}</span>
                          )}
                        </td>
                        <td className="py-2 px-2 text-slate-600">{v.device}</td>
                        <td className="py-2 px-2 text-center">
                          {v.telegram > 0 ? (
                            <span className="inline-flex items-center justify-center w-6 h-6 bg-sky-100 text-sky-700 rounded-full text-xs font-medium">
                              {v.telegram}
                            </span>
                          ) : (
                            <span className="text-slate-300">-</span>
                          )}
                        </td>
                        <td className="py-2 px-2 text-center">
                          {v.whatsapp > 0 ? (
                            <span className="inline-flex items-center justify-center w-6 h-6 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                              {v.whatsapp}
                            </span>
                          ) : (
                            <span className="text-slate-300">-</span>
                          )}
                        </td>
                        <td className="py-2 px-2 text-slate-600">
                          {v.clicked_managers && v.clicked_managers.length > 0 ? (
                            <span className="text-sm">{v.clicked_managers.join(', ')}</span>
                          ) : (
                            <span className="text-slate-300">-</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between mt-4 pt-4 border-t">
                <p className="text-sm text-slate-500">
                  Стр. {visitorPage} из {visitorPageCount} ({visitorCount} всего)
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setVisitorPage(p => Math.max(1, p - 1))}
                    disabled={visitorPage === 1}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setVisitorPage(p => Math.min(visitorPageCount, p + 1))}
                    disabled={visitorPage === visitorPageCount}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Traffic Chart (at the end) */}
      <Card>
        <CardHeader>
          <CardTitle>Трафик за 7 дней</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-64">
            {traffic.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={traffic}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis
                    dataKey="date"
                    tickFormatter={(val) => val.slice(5)}
                    stroke="#94a3b8"
                    fontSize={12}
                  />
                  <YAxis stroke="#94a3b8" fontSize={12} />
                  <Tooltip
                    contentStyle={{
                      background: 'white',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px'
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="visits"
                    stroke="#0f172a"
                    strokeWidth={2}
                    dot={{ fill: '#0f172a' }}
                    name="Визиты"
                  />
                  <Line
                    type="monotone"
                    dataKey="clicks"
                    stroke="#0EA5E9"
                    strokeWidth={2}
                    dot={{ fill: '#0EA5E9' }}
                    name="Клики"
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-slate-400">
                Нет данных
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
